import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockContainer from '../components/GuideMockContainer';
import GuideMockTextField from '../components/GuideMockTextField';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideProjectUpdate: React.FC = () => {
  return (
    <GuidePageLayout
      title="プロジェクト情報を更新する"
      description="既存プロジェクトの情報を変更します（ステータス変更、情報修正等）。"
      breadcrumbs={[{ label: 'プロジェクト管理', path: '/guide/project/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
            ['対象プロジェクト', '存在し、ステータスが「終了」でないこと'],
          ]}
        />
      </GuideSection>

      <GuideSection title="画面モード">
        <GuideTable
          headers={['モード', 'タイトル', '説明']}
          rows={[
            [
              '参照モード',
              'プロジェクト詳細',
              'プロジェクト情報を参照専用で表示。編集ボタンで編集モードに切替',
            ],
            [
              '編集モード',
              'プロジェクト詳細（編集中）',
              '各フィールドが入力可能になる。戻る矢印で参照モードに戻る',
            ],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: 'プロジェクト管理画面を開く' },
            { label: 'プロジェクト一覧から対象プロジェクトを検索・選択する' },
            {
              label: '参照モード（プロジェクト詳細）が表示される',
              description:
                'ヘッダー右上にプロジェクトIDが表示されます。コピーボタンでクリップボードにコピーできます。',
              content: (
                <GuideMockContainer label="参照モード（ヘッダー部分）">
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        プロジェクト詳細
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 'auto' }}>
                        <Typography variant="caption" color="text.secondary">
                          ID: xxxxxxxx-xxxx-xxxx-xxxx
                        </Typography>
                        <IconButton size="small">
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「編集」ボタンを押下して編集モードに切り替える',
              content: (
                <GuideMockContainer label="参照モード フッター">
                  <GuideMockButton label="編集" startIcon={<EditIcon />} />
                </GuideMockContainer>
              ),
            },
            {
              label: '編集フォームで変更したい項目を修正する',
              content: (
                <GuideMockContainer label="編集モード">
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        プロジェクト詳細（編集中）
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <GuideMockTextField
                          label="プロジェクト名"
                          required
                          value="サンプルプロジェクト"
                        />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="開始日（年度月選択）" />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="終了日（年度月選択）" />
                      </Grid>
                    </Grid>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「更新」ボタンを押下する',
              content: (
                <GuideMockContainer label="編集モード フッター">
                  <GuideMockButton label="更新" startIcon={<SaveIcon />} />
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="ステータス遷移ルール">
        <GuideTable
          headers={['現在のステータス', '遷移先', '条件']}
          rows={[
            ['準備中', '稼働中', '開始日の設定が必須'],
            ['準備中 / 稼働中', '終了', '終了日の設定が必須'],
            ['稼働中', '準備中', '逆戻り不可'],
            ['終了', '—', '変更不可（編集ボタン非表示）'],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', 'モード', '動作']}
          rows={[
            ['← 戻る矢印（参照モード）', '参照', '詳細画面を閉じて一覧に戻る'],
            ['IDコピーボタン', '共通', 'プロジェクトIDをクリップボードにコピー'],
            ['編集ボタン', '参照', '編集モードに切り替える'],
            ['← 戻る矢印（編集モード）', '編集', '編集内容を破棄して参照モードに戻る'],
            ['更新ボタン', '編集', '入力内容でプロジェクト情報を更新する'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox type="warning">
            マネージャー・取引先は変更できません（登録時に確定）。
          </GuideInfoBox>
          <GuideInfoBox type="warning">稼働中から準備中への逆戻りはできません。</GuideInfoBox>
          <GuideInfoBox>業界・技術スタックは保存時に洗い替えされます。</GuideInfoBox>
          <GuideInfoBox>
            編集モードで戻る矢印をクリックすると、未保存の変更は破棄されます。
          </GuideInfoBox>
          <GuideInfoBox>
            開始日・終了日は年度月選択（FinancialYearMonthSelect）から選択します。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideProjectUpdate;
